import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { BreadcrumbConfig } from "@/config/documentViewConfig";
import { formatBreadcrumbDisplayLabel } from "@/lib/navigation/formatBreadcrumbDisplayLabel";
import {
  getStack,
  popToPreviousHistoryFrame,
  truncateStackTo,
  subscribeNavigationStack,
  type NavigationStackFrame,
} from "@/lib/navigation/documentNavigationStack";

export type UseDocumentViewerBreadcrumbsOptions = {
  /** Fallback title while the stack top label is loading. */
  fallbackTitle: string;
  resolveBaseReturnUrl: () => string;
  /** Called only when Tilbage exits past the stack root. */
  onExitToList?: () => void;
  /** Reattach datasource filter handler for Kilder-entry legislation HTML. */
  getDatasourceFrameOnClick?: (
    datasourceId: string
  ) => (() => void) | undefined;
};

const frameToBreadcrumb = (
  frame: NavigationStackFrame,
  index: number,
  setStackState: (stack: NavigationStackFrame[]) => void,
  getDatasourceFrameOnClick?: (datasourceId: string) => (() => void) | undefined
): BreadcrumbConfig => ({
  title: formatBreadcrumbDisplayLabel(frame.label),
  fullTitle: frame.label,
  url: frame.url,
  widthTier: frame.frameKind === "document" ? "document" : "anchor",
  onClick: () => {
    truncateStackTo(index);
    setStackState(getStack());
    if (frame.frameKind === "datasource" && frame.datasourceId) {
      getDatasourceFrameOnClick?.(frame.datasourceId)?.();
    }
  },
});

export const useDocumentViewerBreadcrumbs = ({
  fallbackTitle,
  resolveBaseReturnUrl,
  onExitToList,
  getDatasourceFrameOnClick,
}: UseDocumentViewerBreadcrumbsOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stack, setStackState] = useState<NavigationStackFrame[]>(() =>
    getStack()
  );

  useEffect(() => {
    setStackState(getStack());
  }, [location.key]);

  useEffect(
    () => subscribeNavigationStack(() => setStackState(getStack())),
    []
  );

  const flatParentBreadcrumbs = useMemo((): BreadcrumbConfig[] => {
    return stack
      .slice(0, -1)
      .map((frame, index) =>
        frameToBreadcrumb(
          frame,
          index,
          setStackState,
          getDatasourceFrameOnClick
        )
      );
  }, [stack, getDatasourceFrameOnClick]);

  const topFrame = stack[stack.length - 1];
  const currentFullTitle = topFrame?.label || fallbackTitle;
  const currentTitle = formatBreadcrumbDisplayLabel(currentFullTitle);

  const handleViewerBack = useCallback(() => {
    if (getStack().length > 1) {
      const newTop = popToPreviousHistoryFrame();
      setStackState(getStack());
      if (newTop) {
        navigate(newTop.url);
        return;
      }
    }
    onExitToList?.();
    navigate(resolveBaseReturnUrl());
  }, [navigate, onExitToList, resolveBaseReturnUrl]);

  return {
    parentBreadcrumbs: flatParentBreadcrumbs,
    flatParentBreadcrumbs,
    currentTitle,
    currentFullTitle,
    handleViewerBack,
  };
};
